import { useState, useEffect } from "react";
import { useAuth } from "@/lib/AuthContext";
import { base44 } from "@/api/base44Client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
// ArrowLeft removed — back button now lives in the top nav.
import { LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [emailNotifications, setEmailNotifications] = useState(user?.email_notifications ?? true);
  const [useLbs, setUseLbs] = useState(user?.use_lbs ?? true);
  const [useInches, setUseInches] = useState(user?.use_inches ?? true);
  const [timeFormat, setTimeFormat] = useState(user?.time_format === "24h" ? "24h" : "12h");
  const [saving, setSaving] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    setEmailNotifications(user?.email_notifications ?? true);
    setUseLbs(user?.use_lbs ?? true);
    setUseInches(user?.use_inches ?? true);
    setTimeFormat(user?.time_format === "24h" ? "24h" : "12h");
  }, [user]);

  const hasChanges = 
    emailNotifications !== (user?.email_notifications ?? true) ||
    useLbs !== (user?.use_lbs ?? true) ||
    useInches !== (user?.use_inches ?? true) ||
    timeFormat !== (user?.time_format === "24h" ? "24h" : "12h");

  const handleSave = async () => {
    setSaving(true);
    try {
      await base44.auth.updateMe({
        email_notifications: emailNotifications,
        use_lbs: useLbs,
        use_inches: useInches,
        time_format: timeFormat,
      });
      await refreshUser();
      toast({
        title: "Settings saved",
        description: "Your preferences have been updated.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save settings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-md mx-auto space-y-6 pt-2">
      {/* Back button intentionally lives in the top-nav (handled in
          TopBar.jsx — see `onSettingsPage` branch). Routes back to the
          My Account page, not the home dashboard. */}
      <h1 className="text-2xl font-display font-bold">Settings</h1>

      {/* Notifications */}
      <Card className="p-4 border-0 shadow-sm">
        <div className="space-y-4">
          <h2 className="font-semibold text-sm">Notifications</h2>
          <div className="flex items-center justify-between">
            <Label htmlFor="email-notif" className="font-normal cursor-pointer">
              Email Notifications
            </Label>
            <Switch
              id="email-notif"
              checked={emailNotifications}
              onCheckedChange={setEmailNotifications}
            />
          </div>
        </div>
      </Card>

      {/* Payment methods section removed — billing is handled on the
          marketing site at gotafitness.com, not inside the member app. */}

      {/* Preferences */}
      <Card className="p-4 border-0 shadow-sm">
        <div className="space-y-4">
          <h2 className="font-semibold text-sm">Measurement Preferences</h2>
          <div className="flex items-center justify-between">
            <Label htmlFor="weight-unit" className="font-normal cursor-pointer">
              Weight Unit
            </Label>
            <div className="flex items-center gap-2">
              <span className={`text-sm ${!useLbs ? "text-muted-foreground" : ""}`}>lbs</span>
              <Switch
                id="weight-unit"
                checked={!useLbs}
                onCheckedChange={(val) => setUseLbs(!val)}
              />
              <span className={`text-sm ${useLbs ? "text-muted-foreground" : ""}`}>kg</span>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="height-unit" className="font-normal cursor-pointer">
              Height Unit
            </Label>
            <div className="flex items-center gap-2">
              <span className={`text-sm ${!useInches ? "text-muted-foreground" : ""}`}>in</span>
              <Switch
                id="height-unit"
                checked={!useInches}
                onCheckedChange={(val) => setUseInches(!val)}
              />
              <span className={`text-sm ${useInches ? "text-muted-foreground" : ""}`}>cm</span>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="time-format" className="font-normal cursor-pointer">
              Time Format
            </Label>
            <div className="flex items-center gap-2">
              <span className={`text-sm ${timeFormat !== "12h" ? "text-muted-foreground" : ""}`}>12h</span>
              <Switch
                id="time-format"
                data-testid="settings-time-format-switch"
                checked={timeFormat === "24h"}
                onCheckedChange={(val) => setTimeFormat(val ? "24h" : "12h")}
              />
              <span className={`text-sm ${timeFormat !== "24h" ? "text-muted-foreground" : ""}`}>24h</span>
            </div>
          </div>
        </div>
      </Card>

      <Button
        onClick={handleSave}
        disabled={saving || !hasChanges}
        className="w-full"
      >
        {saving ? "Saving..." : "Save Changes"}
      </Button>

      <Button
        variant="outline"
        className="w-full text-destructive border-destructive/30 hover:bg-destructive/5"
        onClick={() => setShowLogoutConfirm(true)}
        data-testid="settings-logout-btn"
      >
        <LogOut className="w-4 h-4 mr-2" />
        Log Out
      </Button>

      <AlertDialog open={showLogoutConfirm} onOpenChange={setShowLogoutConfirm}>
        <AlertDialogContent data-testid="logout-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Log out of TA Fitness?</AlertDialogTitle>
            <AlertDialogDescription>
              You&apos;ll need to log in again on this device to access your account.
              Your data stays safe.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loggingOut} data-testid="logout-cancel">
              Stay logged in
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="logout-confirm"
              disabled={loggingOut}
              onClick={async (e) => {
                e.preventDefault();
                setLoggingOut(true);
                await base44.auth.logout();
                navigate("/login", { replace: true });
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {loggingOut ? "Logging out…" : "Log out"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}